import moment from 'moment';

let instance;
let dataUsage;
let chartOptions;
let dataReversed = false;
let daysStore = 30;
let categoryStore = 'special';

export default function trafficGoogleChart(app)
{
    if (typeof google === 'undefined') return;
    // google is imported by adding static.com/charts/loader in html header from the component
    google.charts.load('current', {'packages':['corechart']});

    let download = []
    let upload = []
    let labels = []
    let total = []
    let requests = []

    const getRange = daysInPast => {

        // Current month
        if(daysInPast === 30) return {group: 'days', start: 30, end: 0}

        // Last month
        if(daysInPast === 60) return {group: 'days', start: 60, end: 30}

        // Last 3 months
        if(daysInPast === 90) return {group: 'weeks', start: 90, end: 0}

        // Last 6 months
        if(daysInPast === 180) return {group: 'weeks', start: 180, end: 0}

        // Last 12 months
        if(daysInPast === 360) return {group: 'weeks', start: 360, end: 0}

    }

    /**
     * Group the data by days; used for current month and last month
     *
     * @param range
     * @param data
     */

    const groupDataByDays = (range, data) => {

        // For each day
        for(let i = range.start; i >= range.end; i--) {

            // Get the label
            const label = moment().subtract(i, 'days').format('YYYY-MM-DD')

            // Format the label
            const niceLabel = moment(label).format('MMM D, Y')

            // Store the label
            labels.push(niceLabel)

            const daysData = getElements(data);

            // Get the data
            // const seriesData = data[label] || {}
            const seriesData = daysData[i] || {}
            //console.log(i, niceLabel, seriesData);
            download.push(seriesData.download || 0)
            upload.push(seriesData.upload || 0)
            total.push(seriesData.total || 0)
            requests.push(seriesData.requests || 0)

        }

    }

    /**
     * Add or update the value in storage by label
     *
     * @param storage
     * @param label
     * @param value
     */

    const addOrUpdateStorage = (storage, label, value) => {

        let found = false
        value = parseInt(value)

        for(let i = 0; i < storage.length; i++) {

            const record = storage[i]

            if(record.meta === label) {
                found = true
                storage[i].value += value
            }

        }

        if(found === false) storage.push({meta: label, value})

        return storage

    }

    /**
     * Group the data by weeks; used for last 3/6/12 months
     *
     * @param range
     * @param data
     */

    const groupDataByWeeks = (range, data) => {

        // For storing the data by week
        let downloadWeek = []
        let uploadWeek = []
        let totalWeek = []
        let totalRequests = []

        // For each day
        for(let i = range.start; i >= range.end; i--) {

            // Get the date
            const currentDate = moment().subtract(i, 'days')

            // Get the label
            const label = currentDate.format('YYYY-MM-DD')

            // Get the year and the week
            const yearWeek = currentDate.format('gggg w').split(' ')

            // Get the week start day
            const weekStartDay = currentDate.weekday(1).format('MMM DD')

            // Get the week end day
            const weekEndDay = currentDate.weekday(7).format('MMM DD')

            // Get the year
            const year = yearWeek[0]

            // Get the nice label
            const niceLabel = `${weekStartDay}-${weekEndDay}, ${year}`

            // Store the label
            if(labels.indexOf(niceLabel) === -1) labels.push(niceLabel)

            const daysData = getElements(data);

            // Get the data
            // const seriesData = data[label] || {}
            const seriesData = daysData[i] || {}

            downloadWeek = addOrUpdateStorage(downloadWeek, niceLabel, seriesData.download || 0)
            uploadWeek = addOrUpdateStorage(uploadWeek, niceLabel, seriesData.upload || 0)
            totalWeek = addOrUpdateStorage(totalWeek, niceLabel, seriesData.total || 0)
            totalRequests = addOrUpdateStorage(totalRequests, niceLabel, seriesData.total || 0)

        }

        download = downloadWeek.map( week => week.value)
        upload = uploadWeek.map( week => week.value)
        total = totalWeek.map( week => week.value)
        requests = totalRequests.map( week => week.value)

    }

    const getElements = (data) => {
        if(data.elements) return data.elements;
        if(categoryStore === 'basic' && data.basic?.elements) return data.basic.elements;
        if(categoryStore === 'special' && data.special?.elements) return data.special.elements;
        if(categoryStore === 'fast' && data.fast?.elements) return data.fast.elements;

        return [];
    }

    const formatBandwidth = function(value, format) {
        if(format === 'TB') return parseFloat((value / 1000000).toFixed(2))  // + ' TB'
        if(format === 'GB') return parseFloat((value / 1000).toFixed(2))  // + ' GB'
        return parseFloat(value) // + ' MB'
    }

    const formatBandwidthTooltip = function(value) {
        if(value > 1000000) return (value / 1000000).toFixed(2) + ' TB'
        if(value > 1000) return (value / 1000).toFixed(2) + ' GB'
        return value  + ' MB'
    }

    const formatVAxisBandwidth = function (data){
        let currentData = getElements(data);
        let highVal = 0;
        currentData.forEach(elem => {
            if(elem.total > highVal) highVal = elem.total;
        });

        if(highVal > 1000000) return 'TB'
        if(highVal > 1000) return 'GB'
        return 'MB'
    }

    /**
     * Format the chart data
     *
     * @param data
     * @param daysInPast
     */

    const formatData = function(data, daysInPast = 30)
    {
        if(!dataReversed){
            if(data.elements) data.elements.reverse();
            if(data.basic?.elements) data.basic.elements.reverse();
            if(data.special?.elements) data.special.elements.reverse();
            if(data.fast?.elements) data.fast.elements.reverse();
            dataReversed = true;
        }
        // Get the range
        const range = getRange(daysInPast)

        // Restore previously stored data
        download = []
        labels = []
        upload = []
        total = []
        requests = []

        // Group data by days
        if(range.group === 'days') groupDataByDays(range, data)
        if(range.group === 'weeks') groupDataByWeeks(range, data)

        let dataT = new google.visualization.DataTable();

        dataT.addColumn('number', 'Date');
        dataT.addColumn('number', "Download");
        dataT.addColumn({type: 'string', role: 'tooltip', 'p': {'html': true}});
        dataT.addColumn('number', "Upload");
        dataT.addColumn({type: 'string', role: 'tooltip', 'p': {'html': true}});
        dataT.addColumn('number', "Total");
        dataT.addColumn({type: 'string', role: 'tooltip', 'p': {'html': true}});
        dataT.addColumn('number', "Connections");
        dataT.addColumn({type: 'string', role: 'tooltip', 'p': {'html': true}});


        let rows = [];
        let format = formatVAxisBandwidth(data);
        for (const l in labels) {
            let tooltip = `<div class="g-tooltip">${labels[l]}<br> 
                        Download: <b>${formatBandwidthTooltip(download[l])}</b><br> 
                        Upload: <b>${formatBandwidthTooltip(upload[l])}</b><br> 
                        Total: <b>${formatBandwidthTooltip(total[l])}</b></div>`

            let barsTooltip = `<div class="g-tooltip">${labels[l]}<br> 
                                Connections: <b>${requests[l].toLocaleString("en-US")}</b><br> 
                                </div>`

            let dateLabel = parseInt(l) + 1;

            let row = [
                dateLabel,
                formatBandwidth(download[l], format),
                tooltip,
                formatBandwidth(upload[l], format),
                tooltip,
                formatBandwidth(total[l], format),
                tooltip,
                requests[l],
                barsTooltip
            ];

            rows.push(row);
        }

        //console.log(rows);

        dataT.addRows(rows);

        return dataT;
    }

    const setChartOptions = function (data) {
        let format = formatVAxisBandwidth(data);

        return {
            title: '',
            //width: 840,
            height: 300,
            curveType: "function",
            lineWidth: 2,
            pointSize: 8,
            pointShape: 'square',
            enableInteractivity: true,
            tooltip: {isHtml: true, trigger: 'hover'},
            chartArea: {width: '86%', height: '80%'},
            legend: 'none',
            fontName: 'campton,sans-serif',
            colors: [
                //'#135EF1',
                '#2C5DB5',
                '#80ABED',
                '#929DB1',
                //'#00c3b3',
                '#cfd9e8'
            ],
            // Gives each series an axis that matches the vAxes number below.
            series: {
                0: {targetAxisIndex: 0},
                1: {targetAxisIndex: 0},
                2: {targetAxisIndex: 0},
                3: {targetAxisIndex: 1, type: 'bars'},
            },
            vAxes: {
                0: {format: `### ${format}`, textStyle: {color: '#a6acc1', fontSize: '12'}},
                1: {format: 'short', textStyle: {color: '#a6acc1', fontSize: '12'}}
            },
            hAxis: {
                textStyle: {color: 'transparent'},
                textPosition: 'none',
                gridlines: {
                    color: '#e8eef3',
                    count: labels.length
                },
                minorGridlines: {
                    color: 'transparent',
                },
            },
            vAxis: {
                viewWindowMode: "explicit",
                viewWindow: {min: 0},
                gridlines: {
                    color: '#E7EDF2',
                },
                minorGridlines: {
                    color: '#E7EDF2',
                    multiple: 2

                },
                textStyle: {color: '#a6acc1'}
            }
        }
    }
    const checkEmptyData = function()
    {
        return !!(upload.every(item => item === 0) &&
            download.every(item => item === 0) &&
            requests.every(item => item === 0));
    }

    const displayNoData = function($chart)
    {
        $chart.hide();
        $('#data-usage-customer-g-legend').hide();
        $('#no-traffic-data').show();
    }

    const drawChart = function(formattedData, options)
    {
        let chart = $('#data-usage-customer-g');

        if(checkEmptyData()){
            displayNoData(chart);
            return;
        }
        $('#no-traffic-data').hide();
        chart.show();
        $('#data-usage-customer-g-legend').show();

        if(!instance) initGChart(dataUsage, $('#data-usage-customer-g'))

        instance.draw(formattedData, options);
    }

    const initGChart = function (data, $chart)
    {
        instance = new google.visualization.LineChart($chart[0]);
        google.visualization.events.addListener(instance, 'ready', function () {
            var gridLines = instance.getContainer().getElementsByTagName('rect');
            Array.prototype.forEach.call(gridLines, function(line) {
                if (line.getAttribute('fill') === '#e8eef3') {
                    line.setAttribute('fill', 'url(#pattern-fill) #e8eef3');
                }
            });
        });
    }

    /**
     * Handle the chart data success event
     *
     * @param data
     * @param $chart
     */

    const onSuccess = function(data, $chart)
    {
        let formattedData = formatData(data);
        let options = setChartOptions(data);

        if(checkEmptyData()){
            displayNoData($chart);
            return;
        }

        initGChart(data, $chart)

        drawChart(formattedData, options);
    }

    /**
     * Setup the chart
     *
     * @param $chart
     */

    const setup = function($chart)
    {
        // Get the data source url
        const dataUrl = $chart.data('url')

        // Get the data
        const promise = $.ajax({
            url: dataUrl,
            dataType: "json",
            type: "GET",
            contentType: "application/json; charset=utf-8",
            success: function(data)
            {
                dataUsage = data
                onSuccess(data, $chart)
            }
        })

        // On failure
        promise.fail(function(){
            displayNoData()
        })
    }

    /**
     * Update the chart with new data
     *
     * @param event
     */

    const onDataUsageSortChange = function(event)
    {
        // Stop if no data or no chart
        if(!dataUsage || !instance) return

        daysStore = parseInt(event.value);
        let options = setChartOptions(dataUsage);

        // Update the chart data
        drawChart(formatData(dataUsage, daysStore), options)
    }

    const onDataCategorySortChange = function (event)
    {
        categoryStore = event.value;
        let options = setChartOptions(dataUsage);
        $('#membership-category-sort').val(categoryStore).change();
        drawChart(formatData(dataUsage, daysStore), options)
    }

    const onDataCategorySortChangeByUsage = function (category)
    {
        categoryStore = category;
        let options = setChartOptions(dataUsage);
        drawChart(formatData(dataUsage, daysStore), options)
    }

    $(function(){

        // Get the chart container
        const $chart = $('#data-usage-customer-g')

        // Stop if no chart found
        if($chart.length === 0) return

        // Setup the chart
        setup($chart)

        $('#data-category-sort-by-capacity').on('change', function () {
            $('#data-category-sort').val($(this).val()).change()
            onDataCategorySortChangeByUsage($(this).val())
        });

        app.events.on('kx_dropdownSelect:data-usage-sort', onDataUsageSortChange)
        app.events.on('kx_dropdownSelect:data-category-sort', onDataCategorySortChange)

    })

}
